import { E2E_dispatchNonWorkflowActionsSynced } from '../E2E/E2E.dispatch'
import { Flow_downloadCollaborativeReports } from '../Flow/Flow.downloadCollaborativeReports'
import { Flow_syncOngoingReports } from '../Flow/Flow.syncOngoingReports'
import { Flow_uploadNewReports } from '../Flow/Flow.uploadNewReports'
import { IDB_flowActionReports } from '../IDB/IDB.dexie'
import type { Rpts_ExhaustiveAnswer } from '../Rpts/Rpts.ExhaustiveAnswer'
import type { Answer as ServerExhaustiveAnswer } from '../../../shared/domain/reports/types.no-deps'
import { isOnline } from '../Serv/Serv.socketClient'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { Serv_clientWithLogger } from '../Serv/Serv.client'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import type { Flow_ActionReport } from './Flow.ActionReport'
import { Training_SyncCompletions } from '../Training/Training_Completions.sync'

export async function Flow_sync() {
    const logger = Logg_createLogger('Flow_sync')
    try {
        // Do not sync if offline
        if (!(await isOnline())) {
            logger.log(`skipping Flow_sync as offline`)
            return
        }

        logger.log(`syncing actions`)
        await syncNonWorkflowActions(logger)

        logger.log(`upload new reports`)
        await Flow_uploadNewReports(logger)

        logger.log(`download reports from collaborators`)
        await Flow_downloadCollaborativeReports(logger)

        logger.log(`syncing ongoing reports`)
        await Flow_syncOngoingReports(logger)

        // We want to sync training completions after completed reports are synced.
        // Otherwise the training completion sync might incorrectly override local values.
        logger.log(`syncing training completions`)
        await Training_SyncCompletions(logger)

        logger.log(`success`)
    } catch (error) {
        logger.error(error)
    }
}

async function syncNonWorkflowActions(logger: ClientLogger) {
    // Fetch all the completed action reports
    const reports = await IDB_flowActionReports().where('status').equals('completed').toArray()

    if (reports.length > 0) {
        logger.log(`found non-workflow actions to upload`, { count: reports.length })
    }

    let successCount = 0

    // For each action report
    for (const report of reports) {
        try {
            await syncNonWorkflowAction(logger, report)
            successCount++
        } catch (error) {
            logger.error(error)
        }
    }

    E2E_dispatchNonWorkflowActionsSynced()

    if (reports.length > 0) {
        logger.log(`finished uploading non-workflow actions`, { count: successCount })
    }
}

export async function syncNonWorkflowAction(
    logger: ClientLogger,
    report: Flow_ActionReport,
    signature?: string,
    signedAtTime?: Date
) {
    // Let typescript know that these action reports are completed
    if (report.status !== 'completed') return

    // Do not pass in null coordinates
    const { lat, lon } = report.coordinates
    const coordinates = { lat: lat || 0, lon: lon || 0 }

    // Upload it to the server
    await Serv_clientWithLogger(logger).confirmAction({
        clientId: report.id,
        coordinates,
        scheduledInstance: report.scheduledInstance.id,
        startDate: report.startDate,
        endDate: report.completedDate,
        answers: report.answers.map(mapToServerExhaustiveAnswer),
        teamDoneFor: report.scheduledInstance.teams[0]?.id,
        signature,
        signedAtTime,
    })
    // Then delete it from disk
    await IDB_flowActionReports().delete(report.id)

    logger.log(`deleted local copy of non-workflow action`, { reportClientId: report.id })
}

/**
 * Maps from app answers where an photo might still be pending upload
 */
export function mapToServerExhaustiveAnswer(answer: Rpts_ExhaustiveAnswer): ServerExhaustiveAnswer {
    const { value } = answer

    if (value.type === 'pendingPhotos') {
        throw new Error('Should not have pendingPhoto answers on an action report')
    }

    return { ...answer, value }
}
